import {
    Box,
    Typography,
    useTheme,
    CircularProgress
} from '@mui/material';
import React, { useEffect } from 'react';
import { BarChart } from '@mui/x-charts/BarChart';
import { Moment } from 'moment';
import PortalCard from '../../../../../components/PortalCard';
import { productAnalyticsStore } from '../../../../../app/stores/ProductAnalytics';

interface TopProductsChartProps {
    limit?: number;
    title?: string;
    dateRange?: [Moment, Moment];
}

const TopProductsChart: React.FC<TopProductsChartProps> = ({ 
    limit = 10, 
    title = 'Top 10 Most Viewed Products',
    dateRange
}) => {
    const theme = useTheme();

    const {
        topProducts,
        topProductsLoading,
        topProductsError,
        fetchTopProducts
    } = productAnalyticsStore((state) => ({
        topProducts: state.topProducts,
        topProductsLoading: state.topProductsLoading,
        topProductsError: state.topProductsError,
        fetchTopProducts: state.fetchTopProducts
    }));

    useEffect(() => {
        const startDate = dateRange?.[0]?.toISOString();
        const endDate = dateRange?.[1]?.toISOString();
        fetchTopProducts(limit, startDate, endDate);
    }, [limit, dateRange, fetchTopProducts]);

    const chartData = topProducts;
    const loading = topProductsLoading;
    const error = topProductsError;

    const productNames = chartData.map(item => item.productName);
    const viewCounts = chartData.map(item => item.viewCount);

    return (
        <PortalCard
            sx={{
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Box sx={{ mb: 2 }}>
                <Typography variant='h6' fontFamily='primaryFont' fontWeight='bold' sx={{ mb: 0.5 }}>
                    {title}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                    Products ranked by total views
                </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, minHeight: 200, width: '100%', position: 'relative' }}>
                {loading ? (
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography variant='body2' color='error'>
                            {error}
                        </Typography>
                    </Box>
                ) : chartData.length > 0 ? (
                    <BarChart
                        xAxis={[{
                            data: productNames,
                            scaleType: 'band',
                            tickLabelStyle: {
                                angle: -45,
                                textAnchor: 'end',
                                fontSize: '0.7rem',
                                fill: theme.palette.text.secondary,
                            },
                        }]}
                        series={[{
                            data: viewCounts,
                            label: 'Views',
                            color: theme.palette.primary.main,
                        }]}
                                height={400}
                                margin={{ top: 20, right: 30, bottom: 150, left: 60 }}
                        layout='vertical'
                        slotProps={{
                            legend: {
                                hidden: true,
                            },
                        }}
                        sx={{
                            '& .MuiChartsAxis-tickLabel': {
                                fontSize: '0.7rem',
                                fill: theme.palette.text.secondary,
                            },
                            '& .MuiChartsAxis-line': {
                                stroke: theme.palette.divider,
                            },
                            '& .MuiBarElement-root': {
                                rx: 4,
                                width: '60%',
                            },
                        }}
                    />
                ) : (
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography variant='body2' color='text.secondary'>
                            No product view data available
                        </Typography>
                    </Box>
                )}
            </Box>
        </PortalCard>
    );
};

export default TopProductsChart;

